import { createFeatureSelector, createSelector } from '@ngrx/store';
import { EnvironmentState, EnvironmentType, ServiceVersionInfo, ServiceVersionsState } from './environment.state';

/** Feature key for the environment state slice */
export const ENVIRONMENT_FEATURE_KEY = 'environment';

/** Select the entire environment feature state */
export const selectEnvironmentState = createFeatureSelector<EnvironmentState>(
  ENVIRONMENT_FEATURE_KEY
);

/** Select the application info (null while loading or on error) */
export const selectEnvironmentInfo = createSelector(
  selectEnvironmentState,
  (state) => state.info
);

/** Select whether environment info is loading */
export const selectEnvironmentLoading = createSelector(
  selectEnvironmentState,
  (state) => state.loading
);

/** Select any error that occurred */
export const selectEnvironmentError = createSelector(
  selectEnvironmentState,
  (state) => state.error
);

/** Select whether the banner has been dismissed */
export const selectBannerDismissed = createSelector(
  selectEnvironmentState,
  (state) => state.bannerDismissed
);

/** Helper function to parse environment string to EnvironmentType */
function parseEnvironmentType(environment: string): EnvironmentType {
  const envLower = environment.toLowerCase();
  if (envLower === 'production') return 'production';
  if (envLower === 'staging') return 'staging';
  if (envLower === 'preview') return 'preview';
  if (envLower === 'local') return 'local';
  return 'development';
}

/** Select the environment type for UI styling */
export const selectEnvironmentType = createSelector(
  selectEnvironmentInfo,
  (info): EnvironmentType => {
    if (!info) return 'development';
    return parseEnvironmentType(info.environment);
  }
);

/** Select whether to show the warning banner */
export const selectShowWarningBanner = createSelector(
  selectEnvironmentInfo,
  selectBannerDismissed,
  (info, dismissed): boolean => {
    if (dismissed) return false;
    if (!info) return false;
    // Show warning for non-production environments
    return !info.isProduction;
  }
);

/** Select whether this is a preview environment */
export const selectIsPreview = createSelector(
  selectEnvironmentInfo,
  (info): boolean => info?.prNumber != null
);

/** Select the display label for the environment */
export const selectEnvironmentLabel = createSelector(
  selectEnvironmentInfo,
  (info): string => {
    if (!info) return '';

    if (info.prNumber) {
      return `Preview PR #${info.prNumber}`;
    }

    return info.environment;
  }
);

/** Select the API version */
export const selectApiVersion = createSelector(
  selectEnvironmentInfo,
  (info): string | null => info?.apiVersion ?? null
);

/** Select the full API version (includes pre-release info) */
export const selectApiFullVersion = createSelector(
  selectEnvironmentInfo,
  (info): string | null => info?.apiFullVersion ?? null
);

/** Select the monorepo SHA */
export const selectMonorepoSha = createSelector(
  selectEnvironmentInfo,
  (info): string | null => info?.monorepoSha ?? null
);

// ============================================================================
// Service Version Selectors
// ============================================================================

/** Select service versions state (null if not loaded yet) */
export const selectServiceVersions = createSelector(
  selectEnvironmentState,
  (state): ServiceVersionsState | null => state.serviceVersions
);

/** Select whether service versions are loading */
export const selectServiceVersionsLoading = createSelector(
  selectServiceVersions,
  (versions): boolean => versions?.loading ?? false
);

/**
 * Transform version string - replaces "CHANGEME" and null with local dev message.
 */
function transformVersion(version: string | null): string | null {
  if (!version || version === 'CHANGEME') {
    return 'Local Development (Unversioned)';
  }
  return version;
}

/**
 * Apply version transformation to a ServiceVersionInfo object.
 */
function transformServiceVersionInfo(info: ServiceVersionInfo): ServiceVersionInfo {
  return {
    ...info,
    version: transformVersion(info.version),
  };
}

/** Select web application version info (with transformation) */
export const selectWebVersion = createSelector(
  selectServiceVersions,
  (versions): ServiceVersionInfo | null =>
    versions?.web ? transformServiceVersionInfo(versions.web) : null
);

/** Select API service version info (with transformation) */
export const selectApiServiceVersion = createSelector(
  selectServiceVersions,
  (versions): ServiceVersionInfo | null =>
    versions?.api ? transformServiceVersionInfo(versions.api) : null
);

/** Select PM service version info (with transformation) */
export const selectPmServiceVersion = createSelector(
  selectServiceVersions,
  (versions): ServiceVersionInfo | null =>
    versions?.pm ? transformServiceVersionInfo(versions.pm) : null
);

/** Select Quartz service version info (with transformation) */
export const selectQuartzServiceVersion = createSelector(
  selectServiceVersions,
  (versions): ServiceVersionInfo | null =>
    versions?.quartz ? transformServiceVersionInfo(versions.quartz) : null
);

/**
 * Select S3 Notifier version info (derived from ApplicationInfo).
 * Single source of truth: S3 Notifier version comes from API's ApplicationInfo,
 * not from ServiceVersionsState. This avoids duplicate state.
 */
export const selectS3NotifierVersion = createSelector(
  selectEnvironmentInfo,
  (info): ServiceVersionInfo | null => {
    if (!info) return null;
    return {
      name: 'S3 Notifier (Lambda)',
      version: transformVersion(info.s3NotifierVersion),
      status: info.s3NotifierVersion ? 'healthy' : 'not-configured',
    };
  }
);

/**
 * Select all service versions for display (with transformations applied).
 * Combines ServiceVersionsState services with S3 Notifier derived from ApplicationInfo.
 */
export const selectAllServiceVersionsForDisplay = createSelector(
  selectServiceVersions,
  selectS3NotifierVersion,
  (versions, s3NotifierVersion): ServiceVersionInfo[] | null => {
    if (!versions) return null;
    const services = [
      transformServiceVersionInfo(versions.web),
      transformServiceVersionInfo(versions.api),
      transformServiceVersionInfo(versions.pm),
      transformServiceVersionInfo(versions.quartz),
    ];
    // S3 Notifier comes from ApplicationInfo via selectS3NotifierVersion
    if (s3NotifierVersion) {
      services.push(s3NotifierVersion);
    }
    return services;
  }
);
